import {
  InvoiceDocumentWithSummaryAndAmendments,
  PartialInvoiceDocumentWithSummary,
} from '@/api/services/invoices/searchInvoiceDocumentTable';
import './styles.scss';

import Table from '@/common/Table';
import { INVALID_DATE_STRING } from '@/constants';
import { Invoice } from '@/models/gen/graphql';
import InvoiceHistoryTableCollapsibleRow from '@/pages/InvoiceHistory/InvoiceHistoryTable/InvoiceHistoryTableCollapsibleRow';
import InvoiceHistoryTableHeader, { useInvoiceHistoryTable } from '@/pages/InvoiceHistory/InvoiceHistoryTable/InvoiceHistoryTableHeader';
import { getClasses, getProperty } from '@/utils';
import { useMemo } from 'react';

export type InvoiceHistoryTableProps = {
  data: PartialInvoiceDocumentWithSummary[];
  selected: string[];
  onSelect: (id: string | any[]) => void;
  openInvoice?: (id: number) => void;
  onDownloadInvoice?: (genDocumentId: string) => void;
  onShowInvoiceActivity?: (invoiceId: Invoice['id']) => void;
};

const InvoiceHistoryTable = ({
  data,
  selected,
  onSelect,
  onShowInvoiceActivity,
  onDownloadInvoice,
  openInvoice,
}: InvoiceHistoryTableProps): JSX.Element => {
  const [{ sortBy, sortDirection }, setState] = useInvoiceHistoryTable(({ state, setState }) => [
    {
      sortBy: state.sorting.column,
      sortDirection: state.sorting.direction,
    },
    setState,
  ]);

  const sortedRows = useMemo(() => {
    return [...data].sort((a, b) => {
      if (!sortBy || !sortDirection) return 0;
      let aValue: string | number = getProperty(sortBy, a) as string | number;
      let bValue: string | number = getProperty(sortBy, b) as string | number;

      // Special handling for scheduled field to treat INVALID_DATE_STRING as undefined
      if (sortBy === 'scheduled') {
        aValue = aValue === INVALID_DATE_STRING ? '' : aValue;
        bValue = bValue === INVALID_DATE_STRING ? '' : bValue;
      }

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortBy, sortDirection]);
  return (
    <Table
      className={getClasses('InvoiceHistoryTable')}
      header={<InvoiceHistoryTableHeader selected={selected} onSelect={onSelect} data={data} />}
      showControls
    >
      {sortedRows.map(
        (row: InvoiceDocumentWithSummaryAndAmendments): JSX.Element => (
          <InvoiceHistoryTableCollapsibleRow
            selected={selected}
            invoice={row}
            key={row?.id}
            onSelect={onSelect}
            openInvoice={openInvoice}
            onDownloadInvoice={onDownloadInvoice}
            onShowInvoiceActivity={onShowInvoiceActivity}
          />
        )
      )}
    </Table>
  );
};

export default InvoiceHistoryTable;
